import React, { useState, useRef, useEffect } from 'react';
import { HexAlphaColorPicker } from 'react-colorful';

interface PropertyColorProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

export const PropertyColor: React.FC<PropertyColorProps> = ({
  label,
  value,
  onChange,
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [pickerPosition, setPickerPosition] = useState({ top: 0, left: 0 });
  const pickerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Update picker position when opened
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPickerPosition({
        top: rect.bottom + 4,
        left: rect.left
      });
    }
  }, [isOpen]);

  // Close picker when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (
        pickerRef.current && 
        !pickerRef.current.contains(e.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  return (
    <>
      <div className="flex flex-1 items-center gap-1.5">
        <label className="text-gray-600 dark:text-gray-400 text-sm font-medium w-4 flex-shrink-0">
          {label}
        </label>
        <div className="relative flex-1">
          <button
            ref={buttonRef}
            type="button"
            onClick={() => !disabled && setIsOpen(!isOpen)}
            disabled={disabled}
            className="w-full flex items-center gap-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-white text-sm px-2 py-1.5 rounded border border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 focus:border-primary-500 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
          >
            <div
              className="w-6 h-6 rounded border border-gray-300 dark:border-gray-600 flex-shrink-0 relative overflow-hidden"
              style={{
                background: 'conic-gradient(#d0d0d0 0deg 90deg, #ffffff 90deg 180deg, #d0d0d0 180deg 270deg, #ffffff 270deg)'
              }}
            >
              <div
                className="w-full h-full rounded"
                style={{ backgroundColor: value }}
              />
            </div>
            <span className="flex-1 text-left">{value.toUpperCase()}</span>
          </button>
        </div>
      </div>

      {isOpen && !disabled && (
        <div
          ref={pickerRef}
          className="fixed z-[9999] bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded shadow-lg p-3"
          style={{
            top: `${pickerPosition.top}px`,
            left: `${pickerPosition.left}px`
          }}
        >
          <HexAlphaColorPicker color={value} onChange={onChange} />
        </div>
      )}
    </>
  );
};


